import { useState, useEffect } from "react"
import { useQuery, useMutation } from "urql"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import {
  UserPlus,
  Trash2,
  Download,
  Shield,
  ShieldOff,
  Loader2,
  Plus,
  X,
  Activity,
  KeyRound,
  Copy,
  Check,
  Users,
} from "lucide-react"
import { FeatureGuard } from "@/components/feature-guard"
import { useRetentionCap } from "@/hooks/use-session"
import { Link } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PageHeader } from "@/components/shared/page-header"
import { useServerInfo } from "@/hooks/use-server-info"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import {
  OrganizationsQuery,
  UsersQuery,
  MeQuery,
} from "@/lib/graphql/queries"
import {
  UpdateOrganizationMutation,
  CreateOrganizationMutation,
  DeleteOrganizationMutation,
  ConfigureSsoMutation,
  DisableSsoMutation,
  InviteUserMutation,
  RemoveUserMutation,
  UpdateUserRoleMutation,
  UpdateOrganizationRetentionMutation,
  ExportOrganizationDataMutation,
  DeleteOrganizationDataMutation,
  ConfigureOtlpMutation,
  DisableOtlpMutation,
  ConfigureMetricsExportMutation,
  DisableMetricsExportMutation,
} from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type UserItem = ResultOf<typeof UsersQuery>["users"]["nodes"][number]
const USER_LIMIT = 25

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
] as const

const SSO_PROVIDERS = [
  { value: "saml", label: "SAML" },
  { value: "oidc", label: "OIDC" },
] as const

export function SettingsPage() {
  const { info } = useServerInfo()
  const isSaas = info?.edition === "saas"
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Manage your organization, users, and security settings" />
      <Tabs defaultValue="organization">
        <TabsList>
          <TabsTrigger value="organization">Organization</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="sso">SSO</TabsTrigger>
          {isSaas && <TabsTrigger value="scim">SCIM</TabsTrigger>}
          <TabsTrigger value="data">Data & Retention</TabsTrigger>
          <TabsTrigger value="observability">Observability</TabsTrigger>
        </TabsList>
        <TabsContent value="organization">
          <OrganizationTab />
        </TabsContent>
        <TabsContent value="users">
          <UsersTab />
        </TabsContent>
        <TabsContent value="sso">
          <FeatureGuard
            feature="sso"
            title="Single sign-on"
            description="Let your team sign in with SAML or OIDC."
            icon={KeyRound}
            tier="surge"
          >
            <SSOTab />
          </FeatureGuard>
        </TabsContent>
        {isSaas && (
          <TabsContent value="scim">
            <FeatureGuard
              feature="scim"
              tier="torrent"
              title="SCIM provisioning"
              description="Auto-provision and deactivate users from Okta or Azure AD."
              icon={Users}
            >
              <ScimTab />
            </FeatureGuard>
          </TabsContent>
        )}
        <TabsContent value="data">
          <DataRetentionTab />
        </TabsContent>
        <TabsContent value="observability">
          <FeatureGuard
            feature="observability_export"
            title="Observability export"
            description="Stream metrics and traces to Datadog, New Relic, or any OpenTelemetry backend."
            icon={Activity}
          >
            <ObservabilityTab />
          </FeatureGuard>
        </TabsContent>
      </Tabs>
    </div>
  )
}

function OrganizationTab() {
  const [meResult] = useQuery({ query: MeQuery })
  const [orgsResult, reexecuteOrgs] = useQuery({
    query: OrganizationsQuery,
    variables: { limit: 1 },
  })

  const org = orgsResult.data?.organizations.nodes[0]
  const [, updateOrg] = useMutation(UpdateOrganizationMutation)
  const [, createOrg] = useMutation(CreateOrganizationMutation)
  const [name, setName] = useState("")
  const [slug, setSlug] = useState("")
  const [saving, setSaving] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [newName, setNewName] = useState("")
  const [newSlug, setNewSlug] = useState("")
  const [creating, setCreating] = useState(false)

  if (org && !initialized) {
    setName(org.name)
    setSlug(org.slug)
    setInitialized(true)
  }

  const handleSave = async () => {
    if (!org) return
    setSaving(true)
    const res = await updateOrg({ id: org.id, input: { name, slug } })
    setSaving(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("Organization updated")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  if (orgsResult.fetching) {
    return <LoadingState />
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold">Organization Details</h3>
        <div className="space-y-2">
          <Label htmlFor="org-name">Name</Label>
          <Input
            id="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Organization name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="org-slug">Slug</Label>
          <Input
            id="org-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="organization-slug"
            className="font-mono"
          />
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving || !org}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Changes"}
          </Button>
        </div>
      </div>

      {meResult.data?.me && (
        <div className="rounded-lg glass-card p-6 space-y-3 max-w-lg">
          <h3 className="text-sm font-semibold">Current User</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-muted-foreground">Email</span>
              <p className="font-mono">{meResult.data.me.email}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Name</span>
              <p>{meResult.data.me.name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Role</span>
              <p className="capitalize">{meResult.data.me.role}</p>
            </div>
          </div>
        </div>
      )}

      {!org && !orgsResult.fetching && (
        <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
          <h3 className="text-sm font-semibold">Create Organization</h3>
          <p className="text-sm text-muted-foreground">
            No organization found. Create one to get started.
          </p>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            Create Organization
          </Button>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-4 rounded-lg glass-card p-4">
              <div className="space-y-2">
                <Label htmlFor="new-org-name">Name</Label>
                <Input
                  id="new-org-name"
                  placeholder="My Organization"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-org-slug">Slug</Label>
                <Input
                  id="new-org-slug"
                  placeholder="my-organization"
                  className="font-mono"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setCreating(true)
                  const res = await createOrg({ input: { name: newName, slug: newSlug } })
                  setCreating(false)
                  if (res.error) { toast.error(res.error.message); return }
                  toast.success("Organization created")
                  setCreateOpen(false)
                  setNewName("")
                  setNewSlug("")
                  reexecuteOrgs({ requestPolicy: "network-only" })
                }}
                disabled={creating || !newName || !newSlug}
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function UsersTab() {
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)
  const search = debouncedSearch || undefined

  const [meResult] = useQuery({ query: MeQuery })
  const orgId = meResult.data?.me?.organizationId

  const [usersResult, reexecuteUsers] = useQuery({
    query: UsersQuery,
    variables: { organizationId: orgId, search, limit: USER_LIMIT, offset: 0 },
    pause: !orgId,
  })

  const [, removeUser] = useMutation(RemoveUserMutation)
  const [, updateRole] = useMutation(UpdateUserRoleMutation)
  const [, inviteUser] = useMutation(InviteUserMutation)

  const [inviteOpen, setInviteOpen] = useState(false)
  const [removeId, setRemoveId] = useState<string | null>(null)
  const [removing, setRemoving] = useState(false)
  const [inviteEmail, setInviteEmail] = useState("")
  const [inviteName, setInviteName] = useState("")
  const [inviteRole, setInviteRole] = useState("viewer")
  const [inviting, setInviting] = useState(false)
  const [updatingRoles, setUpdatingRoles] = useState<Set<string>>(new Set())

  const handleRemove = async () => {
    if (!removeId) return
    setRemoving(true)
    const res = await removeUser({ id: removeId })
    setRemoving(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("User removed")
    setRemoveId(null)
    reexecuteUsers({ requestPolicy: "network-only" })
  }

  const handleInvite = async () => {
    if (!orgId) return
    setInviting(true)
    const res = await inviteUser({
      organizationId: orgId,
      input: { email: inviteEmail, name: inviteName, role: inviteRole },
    })
    setInviting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("User invited")
    setInviteOpen(false)
    setInviteEmail("")
    setInviteName("")
    setInviteRole("viewer")
    reexecuteUsers({ requestPolicy: "network-only" })
  }

  const handleRoleChange = async (userId: string, role: string) => {
    setUpdatingRoles((prev) => new Set(prev).add(userId))
    const res = await updateRole({ id: userId, input: { role } })
    setUpdatingRoles((prev) => {
      const next = new Set(prev)
      next.delete(userId)
      return next
    })
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("Role updated")
    reexecuteUsers({ requestPolicy: "network-only" })
  }

  const columns: Column<UserItem>[] = [
    {
      header: "Email",
      cell: (row) => <span className="font-mono text-sm">{row.email}</span>,
    },
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Role",
      cell: (row) => (
        <Select
          value={row.role}
          onValueChange={(v) => handleRoleChange(row.id, v)}
          disabled={updatingRoles.has(row.id)}
        >
          <SelectTrigger className="w-28 text-sm h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => (
              <SelectItem key={r.value} value={r.value}>
                {r.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      header: "Last Login",
      cell: (row) =>
        row.lastLoginAt ? (
          <TimeAgo date={row.lastLoginAt} />
        ) : (
          <span className="text-xs text-muted-foreground">never</span>
        ),
    },
    {
      header: "Joined",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Remove user"
          onClick={(e) => {
            e.stopPropagation()
            setRemoveId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-16 text-right",
    },
  ]

  return (
    <div className="space-y-6 pt-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 rounded-lg glass-card p-3">
          <Input
            placeholder="Search users..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-50 text-sm"
          />
        </div>
        <Button size="sm" onClick={() => setInviteOpen(true)} className="shadow-primary-glow transition-shadow">
          <UserPlus className="h-4 w-4 mr-1.5" />
          Invite User
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={usersResult.data?.users.nodes ?? []}
        keyFn={(row) => row.id}
        pageInfo={usersResult.data?.users.pageInfo}
        loading={usersResult.fetching}
        emptyTitle="No users"
        emptyDescription="Invite users to collaborate on your organization"
      />

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invite User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-4 rounded-lg glass-card p-4">
              <div className="space-y-2">
                <Label htmlFor="invite-email">Email</Label>
                <Input
                  id="invite-email"
                  type="email"
                  placeholder="user@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="invite-name">Name</Label>
                <Input
                  id="invite-name"
                  placeholder="Jane Doe"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={inviting || !inviteEmail}>
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invite"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeId}
        onOpenChange={(open) => { if (!open) setRemoveId(null) }}
        title="Remove User"
        description="This will permanently remove this user from the organization. They will lose all access."
        confirmLabel="Remove"
        onConfirm={handleRemove}
        loading={removing}
        destructive
      />
    </div>
  )
}

function SSOTab() {
  const [orgsResult, reexecuteOrgs] = useQuery({
    query: OrganizationsQuery,
    variables: { limit: 1 },
  })

  const org = orgsResult.data?.organizations.nodes[0]
  const [, configureSso] = useMutation(ConfigureSsoMutation)
  const [, disableSso] = useMutation(DisableSsoMutation)

  const [provider, setProvider] = useState("saml")
  const [metadataUrl, setMetadataUrl] = useState("")
  const [clientId, setClientId] = useState("")
  const [clientSecret, setClientSecret] = useState("")
  const [issuerUrl, setIssuerUrl] = useState("")
  const [saving, setSaving] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)
  const [disabling, setDisabling] = useState(false)

  const handleConfigure = async () => {
    if (!org) return
    setSaving(true)
    const input = {
      provider,
      ...(provider === "saml"
        ? { idpMetadataUrl: metadataUrl }
        : { clientId, clientSecret, issuer: issuerUrl }),
    }
    const res = await configureSso({ organizationId: org.id, input })
    setSaving(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("SSO configured")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  const handleDisable = async () => {
    if (!org) return
    setDisabling(true)
    const res = await disableSso({ organizationId: org.id })
    setDisabling(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("SSO disabled")
    setDisableOpen(false)
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  if (orgsResult.fetching) {
    return <LoadingState />
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Single Sign-On</h3>
          {org?.ssoEnabled && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
              <Shield className="h-3.5 w-3.5" />
              Enabled ({org.ssoProvider})
            </span>
          )}
        </div>

        {org?.ssoEnabled ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              SSO is currently enabled using <strong className="text-foreground">{org.ssoProvider?.toUpperCase()}</strong>.
              Users will be required to authenticate via your identity provider.
            </p>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDisableOpen(true)}
            >
              <ShieldOff className="h-4 w-4 mr-1.5" />
              Disable SSO
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select value={provider} onValueChange={setProvider}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SSO_PROVIDERS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {provider === "saml" ? (
              <div className="space-y-2">
                <Label htmlFor="metadata-url">Metadata URL</Label>
                <Input
                  id="metadata-url"
                  placeholder="https://idp.example.com/metadata.xml"
                  className="font-mono text-xs"
                  value={metadataUrl}
                  onChange={(e) => setMetadataUrl(e.target.value)}
                />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="issuer-url">Issuer URL</Label>
                  <Input
                    id="issuer-url"
                    placeholder="https://accounts.example.com"
                    className="font-mono text-xs"
                    value={issuerUrl}
                    onChange={(e) => setIssuerUrl(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sso-client-id">Client ID</Label>
                  <Input
                    id="sso-client-id"
                    className="font-mono text-xs"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sso-client-secret">Client Secret</Label>
                  <Input
                    id="sso-client-secret"
                    type="password"
                    className="font-mono text-xs"
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                  />
                </div>
              </>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={handleConfigure} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Configure SSO"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={disableOpen}
        onOpenChange={(open) => { if (!open) setDisableOpen(false) }}
        title="Disable SSO"
        description="This will disable Single Sign-On. Users will need to use other authentication methods."
        confirmLabel="Disable SSO"
        onConfirm={handleDisable}
        loading={disabling}
        destructive
      />
    </div>
  )
}

type ScimToken = {
  id: string
  name: string
  prefix: string
  createdAt: string
  lastUsedAt: string | null
}

const SCIM_BASE_URL = `${window.location.origin}/scim/v2`

function ScimTab() {
  const [loading, setLoading] = useState(true)
  const [entitled, setEntitled] = useState(false)
  const [tokens, setTokens] = useState<ScimToken[]>([])
  const [name, setName] = useState("")
  const [issuing, setIssuing] = useState(false)
  const [revoking, setRevoking] = useState<string | null>(null)
  const [newToken, setNewToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const fetchTokens = async (): Promise<{ entitled: boolean; tokens: ScimToken[] }> => {
    try {
      const res = await fetch("/api/v1/saas/scim/tokens", { credentials: "include" })
      if (!res.ok) return { entitled: false, tokens: [] }
      const body = (await res.json()) as { entitled: boolean; tokens: ScimToken[] }
      return { entitled: body.entitled, tokens: body.tokens ?? [] }
    } catch {
      return { entitled: false, tokens: [] }
    }
  }

  const apply = (data: { entitled: boolean; tokens: ScimToken[] }) => {
    setEntitled(data.entitled)
    setTokens(data.tokens)
  }

  useEffect(() => {
    let cancelled = false
    fetchTokens().then((data) => {
      if (cancelled) return
      apply(data)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const handleIssue = async () => {
    setIssuing(true)
    try {
      const res = await fetch("/api/v1/saas/scim/tokens", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      })
      const body = (await res.json().catch(() => ({}))) as { token?: string; error?: string }
      if (!res.ok) {
        toast.error(body.error || "Couldn't create token")
        return
      }
      setNewToken(body.token ?? null)
      setName("")
      apply(await fetchTokens())
    } catch {
      toast.error("Couldn't reach the server")
    } finally {
      setIssuing(false)
    }
  }

  const handleRevoke = async (id: string) => {
    setRevoking(id)
    try {
      const res = await fetch(`/api/v1/saas/scim/tokens/${id}`, {
        method: "DELETE",
        credentials: "include",
      })
      if (!res.ok && res.status !== 204) {
        toast.error("Couldn't revoke token")
        return
      }
      toast.success("Token revoked")
      apply(await fetchTokens())
    } catch {
      toast.error("Couldn't reach the server")
    } finally {
      setRevoking(null)
    }
  }

  const copyToken = async () => {
    if (!newToken) return
    await navigator.clipboard.writeText(newToken)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  if (loading) {
    return <LoadingState />
  }

  if (!entitled) {
    return (
      <div className="space-y-6 pt-4">
        <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">SCIM Provisioning</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Automatically create, update, and deactivate users from Okta, Azure AD, or
            any SCIM 2.0 identity provider. SCIM is available on the Torrent plan.
          </p>
          <Button asChild size="sm">
            <Link to="/upgrade">View plans</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">SCIM Provisioning</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Point your identity provider at the SCIM base URL below and authenticate with a
          bearer token. Users are provisioned into this organization automatically.
        </p>
        <div className="space-y-2">
          <Label>SCIM base URL</Label>
          <Input readOnly value={SCIM_BASE_URL} className="font-mono text-xs" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="scim-token-name">New token name</Label>
          <div className="flex gap-2">
            <Input
              id="scim-token-name"
              placeholder="Okta production"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button onClick={handleIssue} disabled={issuing}>
              {issuing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" />Create</>}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold">Active tokens</h3>
        {tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tokens yet. Create one to connect your IdP.</p>
        ) : (
          <ul className="divide-y divide-border">
            {tokens.map((t) => (
              <li key={t.id} className="flex items-center justify-between py-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-medium">{t.name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{t.prefix}...</p>
                  <p className="text-xs text-muted-foreground">
                    Created <TimeAgo date={t.createdAt} />
                    {t.lastUsedAt ? <> · last used <TimeAgo date={t.lastUsedAt} /></> : <> · never used</>}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRevoke(t.id)}
                  disabled={revoking === t.id}
                >
                  {revoking === t.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 text-destructive" />}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={newToken !== null} onOpenChange={(open) => { if (!open) setNewToken(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy your SCIM token</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This is the only time the token is shown. Store it in your identity provider now.
            </p>
            <div className="flex gap-2">
              <Input readOnly value={newToken ?? ""} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={copyToken}>
                {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function DataRetentionTab() {
  const navigate = (window as { location: Location }).location
  const [orgsResult, reexecuteOrgs] = useQuery({
    query: OrganizationsQuery,
    variables: { limit: 1 },
  })

  const org = orgsResult.data?.organizations.nodes[0]
  const { maxDays, unlimited } = useRetentionCap()
  const [, updateRetention] = useMutation(UpdateOrganizationRetentionMutation)
  const [, exportData] = useMutation(ExportOrganizationDataMutation)
  const [, deleteData] = useMutation(DeleteOrganizationDataMutation)
  const [, deleteOrg] = useMutation(DeleteOrganizationMutation)

  const [retentionEvents, setRetentionEvents] = useState("")
  const [retentionMessages, setRetentionMessages] = useState("")
  const [savingRetention, setSavingRetention] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [deleteOrgOpen, setDeleteOrgOpen] = useState(false)
  const [deleteOrgConfirmText, setDeleteOrgConfirmText] = useState("")
  const [deletingOrg, setDeletingOrg] = useState(false)
  const [initialized, setInitialized] = useState(false)

  if (org && !initialized) {
    setRetentionEvents(String(org.retentionEvents ?? ""))
    setRetentionMessages(String(org.retentionMessages ?? ""))
    setInitialized(true)
  }

  const clampDays = (v: number) => {
    if (unlimited) return v
    if (v <= 0 || v > maxDays) return maxDays
    return v
  }

  const handleSaveRetention = async () => {
    if (!org) return
    setSavingRetention(true)
    const events = clampDays(retentionEvents ? Number(retentionEvents) : 0)
    const messages = clampDays(retentionMessages ? Number(retentionMessages) : 0)
    setRetentionEvents(String(events))
    setRetentionMessages(String(messages))
    const input = { retentionEvents: events, retentionMessages: messages }
    const res = await updateRetention({ organizationId: org.id, input })
    setSavingRetention(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("Retention settings updated")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  const handleExport = async () => {
    if (!org) return
    setExporting(true)
    const res = await exportData({ organizationId: org.id })
    setExporting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("Data export started. You will be notified when it is ready.")
  }

  const handleDelete = async () => {
    if (!org || deleteConfirmText !== "DELETE ALL DATA") return
    setDeleting(true)
    const res = await deleteData({ organizationId: org.id })
    setDeleting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("All data has been deleted")
    setDeleteOpen(false)
    setDeleteConfirmText("")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  if (orgsResult.fetching) {
    return <LoadingState />
  }

  return (
    <div className="space-y-6 pt-4">
      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold">Data Retention</h3>
        <p className="text-sm text-muted-foreground">
          Configure how long events and messages are retained before automatic deletion.
          {unlimited
            ? " Set to 0 to disable automatic deletion."
            : ` Your plan includes up to ${maxDays} days. `}
          {!unlimited && (
            <Link to="/upgrade" className="text-rose-400 hover:text-rose-300">
              Upgrade for longer retention.
            </Link>
          )}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="retention-events">Events (days)</Label>
            <Input
              id="retention-events"
              type="number"
              min={unlimited ? 0 : 1}
              max={unlimited ? undefined : maxDays}
              placeholder={unlimited ? "90" : String(maxDays)}
              value={retentionEvents}
              onChange={(e) => setRetentionEvents(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="retention-messages">Messages (days)</Label>
            <Input
              id="retention-messages"
              type="number"
              min={unlimited ? 0 : 1}
              max={unlimited ? undefined : maxDays}
              placeholder={unlimited ? "90" : String(maxDays)}
              value={retentionMessages}
              onChange={(e) => setRetentionMessages(e.target.value)}
            />
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={handleSaveRetention} disabled={savingRetention || !org}>
            {savingRetention ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Retention"}
          </Button>
        </div>
      </div>

      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold">Export Data</h3>
        <p className="text-sm text-muted-foreground">
          Export all organization data as a downloadable archive.
        </p>
        <Button variant="outline" onClick={handleExport} disabled={exporting || !org}>
          {exporting ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-1.5" />
          )}
          Export Data
        </Button>
      </div>

      <div className="rounded-lg border border-destructive/30 p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold text-destructive">Danger Zone</h3>
        <p className="text-sm text-muted-foreground">
          Permanently delete all data in this organization. This includes all sources, destinations,
          subscriptions, events, deliveries, applications, endpoints, messages, and related data.
          This action cannot be undone.
        </p>
        <Button
          variant="destructive"
          onClick={() => setDeleteOpen(true)}
          disabled={!org}
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Delete All Data
        </Button>
      </div>

      <Dialog open={deleteOpen} onOpenChange={(open) => { if (!open) { setDeleteOpen(false); setDeleteConfirmText("") } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete All Organization Data</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will permanently delete <strong className="text-foreground">all data</strong> in this organization.
              This action is irreversible.
            </p>
            <div className="space-y-2">
              <Label htmlFor="delete-confirm">
                Type <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">DELETE ALL DATA</code> to confirm
              </Label>
              <Input
                id="delete-confirm"
                className="font-mono"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE ALL DATA"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => { setDeleteOpen(false); setDeleteConfirmText("") }}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting || deleteConfirmText !== "DELETE ALL DATA"}
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete Everything"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border border-destructive/30 p-6 space-y-4 max-w-lg">
        <h3 className="text-sm font-semibold text-destructive">Delete Organization</h3>
        <p className="text-sm text-muted-foreground">
          Permanently delete this organization and all associated data, users, and configuration.
          This action cannot be undone and will terminate all access.
        </p>
        <Button
          variant="destructive"
          onClick={() => setDeleteOrgOpen(true)}
          disabled={!org}
        >
          <Trash2 className="h-4 w-4 mr-1.5" />
          Delete Organization
        </Button>
      </div>

      <Dialog open={deleteOrgOpen} onOpenChange={(open) => { if (!open) { setDeleteOrgOpen(false); setDeleteOrgConfirmText("") } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Organization</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will permanently delete the organization <strong className="text-foreground">{org?.name}</strong> and
              all associated data. All users will lose access. This action is irreversible.
            </p>
            <div className="space-y-2">
              <Label htmlFor="delete-org-confirm">
                Type <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">{org?.slug}</code> to confirm
              </Label>
              <Input
                id="delete-org-confirm"
                className="font-mono"
                value={deleteOrgConfirmText}
                onChange={(e) => setDeleteOrgConfirmText(e.target.value)}
                placeholder={org?.slug ?? ""}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => { setDeleteOrgOpen(false); setDeleteOrgConfirmText("") }}
                disabled={deletingOrg}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!org || deleteOrgConfirmText !== org.slug) return
                  setDeletingOrg(true)
                  const res = await deleteOrg({ id: org.id })
                  setDeletingOrg(false)
                  if (res.error) { toast.error(res.error.message); return }
                  toast.success("Organization deleted")
                  setDeleteOrgOpen(false)
                  navigate.reload()
                }}
                disabled={deletingOrg || deleteOrgConfirmText !== org?.slug}
              >
                {deletingOrg ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete Organization"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

type HeaderEntry = { key: string; value: string }

function ObservabilityTab() {
  const [orgsResult, reexecuteOrgs] = useQuery({
    query: OrganizationsQuery,
    variables: { limit: 1 },
  })

  const org = orgsResult.data?.organizations.nodes[0]
  const [, configureOtlp] = useMutation(ConfigureOtlpMutation)
  const [, disableOtlp] = useMutation(DisableOtlpMutation)
  const [, configureMetricsExport] = useMutation(ConfigureMetricsExportMutation)
  const [, disableMetricsExport] = useMutation(DisableMetricsExportMutation)

  const [endpoint, setEndpoint] = useState("")
  const [headers, setHeaders] = useState<HeaderEntry[]>([])
  const [insecure, setInsecure] = useState(false)
  const [sampleRate, setSampleRate] = useState("1.0")
  const [saving, setSaving] = useState(false)
  const [disableOpen, setDisableOpen] = useState(false)
  const [disabling, setDisabling] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const [meType, setMeType] = useState("datadog")
  const [meApiKey, setMeApiKey] = useState("")
  const [meEndpoint, setMeEndpoint] = useState("")
  const [meInterval, setMeInterval] = useState("30s")
  const [meSaving, setMeSaving] = useState(false)
  const [meDisableOpen, setMeDisableOpen] = useState(false)
  const [meDisabling, setMeDisabling] = useState(false)

  if (org && !initialized) {
    const cfg = org.otlpConfig
    if (cfg) {
      setEndpoint(cfg.endpoint)
      setInsecure(cfg.insecure)
      setSampleRate(String(cfg.sampleRate))
      if (cfg.headers) {
        const entries: HeaderEntry[] = Object.entries(
          cfg.headers as Record<string, string>,
        ).map(([key, value]) => ({ key, value: String(value) }))
        setHeaders(entries.length > 0 ? entries : [])
      }
    }
    const meCfg = org.metricsExportConfig
    if (meCfg) {
      setMeType(meCfg.type)
      setMeApiKey(meCfg.apiKey)
      setMeEndpoint(meCfg.endpoint)
      setMeInterval(meCfg.interval)
    }
    setInitialized(true)
  }

  const addHeader = () => {
    setHeaders((prev) => [...prev, { key: "", value: "" }])
  }

  const removeHeader = (index: number) => {
    setHeaders((prev) => prev.filter((_, i) => i !== index))
  }

  const updateHeader = (index: number, field: "key" | "value", val: string) => {
    setHeaders((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, [field]: val } : entry)),
    )
  }

  const handleSave = async () => {
    if (!org) return
    setSaving(true)

    const headersObj: Record<string, string> = {}
    for (const h of headers) {
      if (h.key.trim()) {
        headersObj[h.key.trim()] = h.value
      }
    }

    const input = {
      endpoint,
      headers: Object.keys(headersObj).length > 0 ? headersObj : null,
      insecure,
      sampleRate: parseFloat(sampleRate) || 1.0,
    }

    const res = await configureOtlp({ organizationId: org.id, input })
    setSaving(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("OTLP configuration saved")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  const handleDisable = async () => {
    if (!org) return
    setDisabling(true)
    const res = await disableOtlp({ organizationId: org.id })
    setDisabling(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    toast.success("OTLP disabled")
    setDisableOpen(false)
    setEndpoint("")
    setHeaders([])
    setInsecure(false)
    setSampleRate("1.0")
    reexecuteOrgs({ requestPolicy: "network-only" })
  }

  if (orgsResult.fetching) {
    return <LoadingState />
  }

  const isConfigured = !!org?.otlpConfig
  const meIsConfigured = !!org?.metricsExportConfig

  return (
    <div className="space-y-6 pt-4">
      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">OpenTelemetry Trace Export</h3>
          {isConfigured && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
              <Activity className="h-3.5 w-3.5" />
              Enabled
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Export distributed traces from Hivehook to your OpenTelemetry-compatible backend
          for end-to-end observability of webhook processing.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="otlp-endpoint">OTLP Endpoint</Label>
            <Input
              id="otlp-endpoint"
              placeholder="https://otel-collector.example.com:4317"
              className="font-mono text-xs"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Headers</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 text-xs"
                onClick={addHeader}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Header
              </Button>
            </div>
            {headers.length > 0 && (
              <div className="space-y-2">
                {headers.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Key"
                      className="font-mono text-xs flex-1"
                      value={entry.key}
                      onChange={(e) => updateHeader(index, "key", e.target.value)}
                    />
                    <Input
                      placeholder="Value"
                      className="font-mono text-xs flex-1"
                      value={entry.value}
                      onChange={(e) => updateHeader(index, "value", e.target.value)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => removeHeader(index)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {headers.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No custom headers configured. Add headers for authentication or routing.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="otlp-sample-rate">Sample Rate</Label>
              <Input
                id="otlp-sample-rate"
                type="number"
                min={0}
                max={1}
                step={0.01}
                placeholder="1.0"
                value={sampleRate}
                onChange={(e) => setSampleRate(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                0.0 to 1.0 (1.0 = 100% of traces)
              </p>
            </div>
            <div className="space-y-2">
              <Label>Connection</Label>
              <div className="flex items-center gap-2 pt-1">
                <Checkbox
                  id="otlp-insecure"
                  checked={insecure}
                  onCheckedChange={(checked) => setInsecure(checked === true)}
                />
                <Label htmlFor="otlp-insecure" className="text-sm font-normal cursor-pointer">
                  Insecure (no TLS)
                </Label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {isConfigured && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDisableOpen(true)}
              >
                Disable OTLP
              </Button>
            )}
            <div className={isConfigured ? "" : "ml-auto"}>
              <Button onClick={handleSave} disabled={saving || !org || !endpoint}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={disableOpen}
        onOpenChange={(open) => { if (!open) setDisableOpen(false) }}
        title="Disable OTLP"
        description="This will disable OpenTelemetry trace export. Traces will no longer be sent to the configured endpoint."
        confirmLabel="Disable OTLP"
        onConfirm={handleDisable}
        loading={disabling}
        destructive
      />

      <div className="rounded-lg glass-card p-6 space-y-4 max-w-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Metrics Export</h3>
          {meIsConfigured && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400">
              <Activity className="h-3.5 w-3.5" />
              {meType === "datadog" ? "Datadog" : "New Relic"}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Export webhook processing metrics to Datadog or New Relic for monitoring and alerting.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Provider</Label>
            <Select value={meType} onValueChange={setMeType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="datadog">Datadog</SelectItem>
                <SelectItem value="newrelic">New Relic</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="me-api-key">API Key</Label>
            <Input
              id="me-api-key"
              type="password"
              placeholder={meType === "datadog" ? "Datadog API key" : "New Relic API key"}
              className="font-mono text-xs"
              value={meApiKey}
              onChange={(e) => setMeApiKey(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="me-endpoint">Endpoint (optional)</Label>
            <Input
              id="me-endpoint"
              placeholder={meType === "datadog" ? "https://api.datadoghq.com/api/v1/series" : "https://metric-api.newrelic.com/metric/v1"}
              className="font-mono text-xs"
              value={meEndpoint}
              onChange={(e) => setMeEndpoint(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Leave blank to use the default endpoint.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="me-interval">Export Interval</Label>
            <Input
              id="me-interval"
              placeholder="30s"
              className="font-mono text-xs w-24"
              value={meInterval}
              onChange={(e) => setMeInterval(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            {meIsConfigured && (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setMeDisableOpen(true)}
              >
                Disable Metrics Export
              </Button>
            )}
            <div className={meIsConfigured ? "" : "ml-auto"}>
              <Button
                onClick={async () => {
                  if (!org) return
                  setMeSaving(true)
                  const input = {
                    type: meType,
                    apiKey: meApiKey,
                    endpoint: meEndpoint || null,
                    interval: meInterval || null,
                  }
                  const res = await configureMetricsExport({ organizationId: org.id, input })
                  setMeSaving(false)
                  if (res.error) { toast.error(res.error.message); return }
                  toast.success("Metrics export configured")
                  reexecuteOrgs({ requestPolicy: "network-only" })
                }}
                disabled={meSaving || !org || !meApiKey}
              >
                {meSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={meDisableOpen}
        onOpenChange={(open) => { if (!open) setMeDisableOpen(false) }}
        title="Disable Metrics Export"
        description="This will stop sending metrics to your provider. Existing data in Datadog or New Relic is unaffected."
        confirmLabel="Disable"
        onConfirm={async () => {
          if (!org) return
          setMeDisabling(true)
          const res = await disableMetricsExport({ organizationId: org.id })
          setMeDisabling(false)
          if (res.error) { toast.error(res.error.message); return }
          toast.success("Metrics export disabled")
          setMeDisableOpen(false)
          setMeApiKey("")
          setMeEndpoint("")
          setMeInterval("30s")
          setMeType("datadog")
          reexecuteOrgs({ requestPolicy: "network-only" })
        }}
        loading={meDisabling}
        destructive
      />
    </div>
  )
}

function LoadingState() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  )
}
